import { getApplies, IApplication } from 'API/applies/applies';
import { APPLY_TYPES } from 'Enum/Applies';
import { useAppSelector } from 'Hooks';
import { replaceZerosWithHyphen } from 'Utils/replaceZerosWithHyphen';
import { compareAsc, compareDesc, set } from 'date-fns';
import { createContext, FC, useEffect, useState } from 'react';

export interface IAplicationsFilters {
  title: string;
  number: string;
  status: string;
  includePhoneRequests: boolean;
  includeWebRequests: boolean;
  from: Date | null;
  to: Date | null;
  year: string;
}

interface IAppliesContext {
  filtersData: IAplicationsFilters;
  setFiltersData: (filtersData: IAplicationsFilters) => void;
  isFilterByYear: boolean;
  setIsFilterByYear: (isFilterByYear: boolean) => void;
  applications: IApplication[];
  filteredApplications: IApplication[];
  applicationsCount: number;
  setApplications: (applications: IApplication[]) => void;
  filterApplications: (filtersData: IAplicationsFilters) => void;
  resetFiltersAndFilteredApplications: () => void;
  isLoading: boolean;
  showFilters: boolean;
}

export const initialFiltersData: IAplicationsFilters = {
  title: '',
  number: '',
  status: '',
  includePhoneRequests: true,
  includeWebRequests: true,
  from: null,
  to: null,
  year: '',
};

export const AppliesContext = createContext<IAppliesContext>({
  filtersData: { ...initialFiltersData },
  isFilterByYear: false,
  setIsFilterByYear: () => {},
  setFiltersData: () => {},
  applications: [],
  filteredApplications: [],
  applicationsCount: 0,
  setApplications: () => {},
  filterApplications: () => [],
  resetFiltersAndFilteredApplications: () => {},
  isLoading: true,
  showFilters: false,
});

interface IAppliesProvider {}

export const AppliesProvider: FC<IAppliesProvider> = (props) => {
  const [applications, setApplications] = useState<IApplication[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<
    IApplication[]
  >([]);
  const [applicationsCount, setApplicationsCount] = useState<number>(0);
  const [filtersData, setFiltersData] = useState<IAplicationsFilters>({
    ...initialFiltersData,
  });
  const [isFilterByYear, setIsFilterByYear] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const initApplication = async () => {
    const applicationsData = await getApplies();
    setIsLoading(false);

    if (applicationsData) {
      applicationsData.sort(
        (a, b) =>
          new Date(b.status.statusDate).getTime() -
          new Date(a.status.statusDate).getTime()
      );

      setApplicationsCount(applicationsData.length);
      setApplications(applicationsData);
      setFilteredApplications(applicationsData);
    }
  };
  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  useEffect(() => {
    if (!isLoading) setIsLoading(true);
    if (showFilters) setShowFilters(false);
    initApplication();
    setFiltersData({
      title: '',
      number: '',
      status: '',
      includePhoneRequests: true,
      includeWebRequests: true,
      from: null,
      to: null,
      year: '',
    });
  }, [imposterId]);

  const filterApplications = (filtersData: IAplicationsFilters) => {
    let filteredApplications = [...applications];

    if (filtersData.title) {
      filteredApplications = filteredApplications.filter(
        (application) => application.subject === filtersData.title
      );
    }

    if (filtersData.number) {
      filteredApplications = filteredApplications.filter(
        (application) =>
          replaceZerosWithHyphen(application.shemeshApplyNumber) ===
          filtersData.number
      );
    }

    if (filtersData.status) {
      filteredApplications = filteredApplications.filter(
        (application) => application.status.description === filtersData.status
      );
    }

    if (!filtersData.includePhoneRequests) {
      filteredApplications = filteredApplications.filter(
        (application) => application.type !== APPLY_TYPES.telephone
      );
    }

    if (!filtersData.includeWebRequests) {
      filteredApplications = filteredApplications.filter(
        (application) => application.type === APPLY_TYPES.telephone
      );
    }

    if (!isFilterByYear && (filtersData.from || filtersData.to)) {
      filteredApplications = filteredApplications.filter((application) => {
        const statusDate = set(new Date(application.status.statusDate), {
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        });
        if (filtersData.from && filtersData.to) {
          return (
            compareAsc(statusDate, filtersData.from) >= 0 &&
            compareDesc(statusDate, filtersData.to) >= 0
          );
        } else if (filtersData.from) {
          return compareAsc(statusDate, filtersData.from) >= 0;
        } else if (filtersData.to) {
          return compareDesc(statusDate, filtersData.to) >= 0;
        } else return false;
      });
    }

    if (isFilterByYear && filtersData.year) {
      filteredApplications = filteredApplications.filter(
        (application) =>
          new Date(application.status.statusDate).getFullYear().toString() ===
          filtersData.year
      );
    }

    setFilteredApplications(filteredApplications);
    setShowFilters(true);
  };

  const resetFiltersAndFilteredApplications = () => {
    setIsLoading(true);
    setFiltersData({ ...initialFiltersData });
    setFilteredApplications([...applications]);
    setIsLoading(false);
    setShowFilters(false);
  };

  useEffect(() => {
    if (JSON.stringify(filtersData) === JSON.stringify(initialFiltersData)) {
      setShowFilters(false);
    }
  }, [filtersData]);

  return (
    <AppliesContext.Provider
      value={{
        filtersData,
        setFiltersData,
        isFilterByYear,
        setIsFilterByYear,
        applications,
        filteredApplications,
        applicationsCount,
        setApplications,
        filterApplications,
        resetFiltersAndFilteredApplications,
        isLoading,
        showFilters,
      }}
    >
      {props.children}
    </AppliesContext.Provider>
  );
};
